import { Component, OnDestroy, OnInit, Output, EventEmitter, Input, NgZone, ViewChild } from '@angular/core';
import { Events } from '../../helpers/events';

// ionic
import { ModalController } from '@ionic/angular';

// other libraries
import { TranslateService, LangChangeEvent } from "@ngx-translate/core";

// rxjs
import { Subject, Subscription } from "rxjs";
import { take, takeUntil, debounceTime } from "rxjs/operators";

// components
import { AutoCompleteComponent } from "../../components/auto-complete/auto-complete.component";

// providers
import { EventService } from "../../services/event.service";
import { ChatGroupsService } from "../../services/chat-groups.service";
import { OverlayService } from "../../services/overlay.service";
import { ParticipantService } from "../../services/participant.service";
import { PlatformService } from "../../services/platform.service";
import { UserService } from "../../services/user.service";

// models
import { Event } from "../../models/event";
import { ChatGroup } from "../../models/chat-group";
import { ChatGroupAdmin } from "../../models/chat-group-admin";
import { Participant } from "../../models/participant";
import { User } from "../../models/user";

// config not using...
import { environment } from "../../../environments/environment";

// helpers
import { log } from '../../helpers/log';

@Component({
    selector: 'app-chat-groups-form',
    templateUrl: './chat-groups-form.component.html',
    styleUrls: ['./chat-groups-form.component.scss'],
})
export class ChagGroupsFormComponent implements OnInit {

    @ViewChild('autocomplete') searchbar: AutoCompleteComponent;

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * subscription for event changes subject
     *
     * @type {Subscription}
     */
    private subscriptionEvent: Subscription;

    /**
     * show form in modal
     *
     * @type boolean
     */
    @Input() modal: boolean = false;

    /**
     * message
     *
     * @type {Message}
     */
    @Input() chatGroup: ChatGroup = new ChatGroup;

    /**
     * event
     *
     * @type {Event}
     */
    public event: Event;

    /**
     * user
     *
     * @type {User}
     */
    public user: User;

    /**
     * user language
     *
     * @type { string}
     */
    public userLang: string;

    /**
     * validate state
     *
     * @type {boolean}
     */
    public validate: boolean = false;

    /**
     * Validation errors
     *
     * @type {object}
     */
    public validationErrors: object = {};

    /**
     * validation errors state
     *
     * @type {boolean}
     */
    public hasValidationErrors: boolean = false;

    /**
     * sending status
     *
     * @type {boolean}
     */
    public sending: boolean = false;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public loading: boolean = false;

    /**
     * is autocomplete list opened?
     *
     * @type {boolean}
     */
    public autocompleteOpen: boolean = false;

    /**
     * is section for attendee invitations opened?
     *
     * @type {boolean}
     */
    public invitationtsOpen: boolean = false;

    /**
     * participants
     *
     * @type {Participant[]}
     */
    public items: Participant[] = [];

    itemsTotal: number = 0;
    itemsLimit: number = 50;
    itemsOffset: number = 0;

    public searchString: string = '';

    /**
     * was chat group changed? created or edited...
     *
     * @type {boolean}
     */
    public edited: boolean = false;

    @Output() onSended: EventEmitter<ChatGroup> = new EventEmitter();
    @Output() onCanceled: EventEmitter<ChatGroup> = new EventEmitter();

    /**
     * available languages
     *
     * @type array
     */
    public langs: any = [];

    /**
     * which select type should be used.. alert/popover/action-sheet
     *
     * @type {string}
     */
    public selectType: string = 'popover';

    /**
     * constructor
     *
     * @param EventService
     * @param TranslateService
     * @param NgZone
     * @param Events
     * @param PlatformService
     * @param OverlayService
     * @param ConversationService
     * @param ModalController
     */
    constructor(
        public eventService: EventService,
        public userService: UserService,
        public translate: TranslateService,
        public ngZone: NgZone,
        public appEvents: Events,
        public plt: PlatformService,
        public overlayService: OverlayService,
        public chatGroupsService: ChatGroupsService,
        public participantService: ParticipantService,
        public modalController: ModalController
    ) {
        this.userLang = this.translate.currentLang;
        if (this.plt.sizeSm) {
            this.selectType = 'action-sheet';
        }
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {
        // user language
        this.langs = this.translate.getLangs();

        this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
            this.userLang = event.lang;
        });

        this.userService.getCurrentUser().pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(0)
        ).subscribe((user) => {
            this.user = user;

            // cancel previous subscription
            if (this.subscriptionEvent) {
                this.subscriptionEvent.unsubscribe();
            }

            this.subscriptionEvent = this.eventService.getCurrentEvent().pipe(
                takeUntil(this.ngUnsubscribe),
                debounceTime(0)
            ).subscribe((event) => {
                this.event = event;
                this.participantService.eventId = this.event.id;

                if (!this.event.can(user.selected_participant.group_id, 'allow_chat_group_creation')) {
                    this.chatGroup.status = 1;
                }

            });
        });

        // reset invitations
        this.chatGroup.participantIds = [];
    }

    /**
     * save chat group form
     *
     * @param {boolean} valid form status
     */
    public saveChatGroup(valid: boolean, invitationtsOpen: boolean = false) {
        if (valid) {
            this.sending = true;
            this.validationErrors = {};
            this.validate = true;

            // store same translations in all translations...
            this.langs.forEach((lang) => {
                this.chatGroup.translateOrNew(lang).name = this.chatGroup.name;
                this.chatGroup.translateOrNew(lang).description = this.chatGroup.description;
            });

            // update for existing
            if (this.chatGroup.id) {
                let subscription = this.chatGroupsService.update(this.chatGroup).subscribe(
                    success => {
                        this.chatGroup.participantIds = [];
                        if (invitationtsOpen) {
                            this.sending = false;
                            this.searchAttendees();
                        } else {
                            this.handleSuccess(success);
                        }
                    },
                    error => {
                        this.handleError(error);
                    });
                this.plt.activeSubscriptions.push(subscription);
            }
            else {
                this.chatGroup.event_id = this.event.id;
                let subscriptionCreate = this.chatGroupsService.create(this.chatGroup).subscribe(
                    success => {
                        // store chat group ID to avoid creating new in next step
                        this.chatGroup.id = success.id;
                        if (invitationtsOpen) {
                            this.sending = false;
                            // when adding invitations for attendees
                            this.searchAttendees();
                        } else {
                            this.handleSuccess(success);
                        }
                    },
                    error => {
                        this.handleError(error);
                    });
                this.plt.activeSubscriptions.push(subscriptionCreate);
            }

        }
    }

    /**
     * send chat group invitations
     *
     */
    public sendInvitations() {

        this.sending = true;
        this.validationErrors = {};
        this.validate = true;

        // send only for existing
        if (this.chatGroup.id) {
            let subscription = this.chatGroupsService.invite(this.chatGroup).subscribe(
                (success) => {
                    this.chatGroup.participantIds = [];
                    this.sending = false;
                    this.searchAttendees();
                    // show result of invitations
                    if (success.messageInvitations) {
                        this.overlayService.showSuccess(success.messageInvitations);
                    }
                    // hide invitations site
                    this.invitationtsOpen = false;
                },
                (error) => {
                    this.handleError(error);
                });
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    /**
     * check app resize (keyboard opening) for input focus
     */
    public autocompleteFocus() {
        this.plt.checkResize();
        // we will hide on small screens all to make autocomplete input bigger
        this.autocompleteOpen = true;
    }


    /**
     * check app resize (keyboard closing) for input blur
     */
    public autocompleteBlur(clear: boolean = false) {
        this.plt.checkResize(false);
        this.autocompleteOpen = false;
        // close autocomplete list when triggering manually cancel for autocomplete
        if (clear) {
            // NOTE[jg] do this only for manual, not on ionBlur for autocomplete component
            // that was causing big performance problems
            this.searchbar.clearValue(true);
        }
    }

    /**
     * Add new moderator to local chat group admins list
     *
     * @param {Particiapnt} particiant event participant
     */
    public addModerator(participant) {

        let exists: boolean = false;
        let moderator = new ChatGroupAdmin;

        moderator.participant = { ...participant };
        moderator.participant_id = participant.id;
        moderator.role = 'moderator';

        this.chatGroup.admins.forEach((admin) => {
            if (admin.participant_id == moderator.participant_id) {
                exists = true;
            }
        })
        if (!exists) {
            this.chatGroup.admins.push(moderator);
        }

        // check resize of app and other items when bluring input
        this.autocompleteBlur();

        // reset value for autocomplet searc
        this.searchbar.clearValue();
    }


    /**
     * Remove moderator from local chat group admins list
     *
     * @param {Participant} moderator event moderator
     */
    public removeModerator(moderator) {
        this.chatGroup.admins.forEach((admin, index) => {
            if (admin.participant_id == moderator.participant_id) {
                this.chatGroup.admins.splice(index, 1);
            }
        });

        if (moderator.id) {
            this.chatGroup.adminsToRemove.push(moderator);
        }
    }

    /**
     * Confirm modal for deleting actual chat grouop
     */
    public deleteChatGroup() {
        // show confirm
        this.overlayService.showConfirm(
            this.translate.instant('CHAT_GROUP_DELETE'),
            this.translate.instant('CHAT_GROUP_DELETE_CONFIRM'),
            () => { this.dropChatGroup() },
            () => { },
            this.translate.instant('YES'),
            this.translate.instant('NO')
        );
    }

    /**
     * list of attendees
     */
    public searchAttendees(reset = true) {

        this.invitationtsOpen = true;

        if (!this.loading) {
            // avoid parallel loading
            this.loading = true;

            if (reset) {
                this.itemsOffset = 0;
                // reset invitations
                this.chatGroup.participantIds = [];
            }

            if (this.chatGroup) {
                let subscription = this.participantService.getFilteredListByEvent(
                    this.event.id,
                    this.itemsLimit,
                    this.itemsOffset,
                    this.searchString,
                    {
                        exludeChatGroupId: this.chatGroup.id,
                        onlyContactable: true
                    },
                    'lastname'
                ).subscribe((response) => {

                    // empty list when reseting, or if there is not enought items for first page
                    if (reset || (this.itemsOffset == 0 && response['participants'].length < this.itemsLimit)) {

                        this.items = [];
                    }

                    // move offset only if there are some participants expected in next page...
                    if (response['participants'].length > 0) {
                        this.itemsOffset += this.itemsLimit;
                    }

                    response['participants'].forEach(item => {
                        this.items.push(new Participant(item));
                    });

                    this.itemsTotal = response['total'];

                    this.loading = false;
                },
                    (error) => {
                        this.loading = false;
                        const data = error.error;
                        this.overlayService.showError(data.message);
                        if (data.leave_group) {
                            this.appEvents.publish('messages:refresh:list');
                        }
                    });
                this.plt.activeSubscriptions.push(subscription);
            }
        }
    }

    /**
     * Add attendee to list for invitations
     *
     * @param {Particiapnt} participant event participant
     */
    public toggleAttendee(participant: Participant) {
        participant.selected = !participant.selected;

        // remove previous record if founded
        this.chatGroup.participantIds.forEach((paticipantId, index) => {
            if (participant.id == paticipantId) {
                this.chatGroup.participantIds.splice(index, 1);
            }
        });

        // insert item if set to be active
        if (participant.selected) {
            this.chatGroup.participantIds.push(participant.id);
        }
    }

    /**
     * Delete chat group on backend
     */
    private dropChatGroup() {
        // dont refresh actual chat group, because it was deleted, not edited...
        this.edited = false;
        // close invitations tab..
        this.invitationtsOpen = false;
        this.chatGroup.event_id = this.event.id;
        let subscription = this.chatGroupsService.delete(this.chatGroup).subscribe(
            success => {
                this.handleSuccess(success);
                this.appEvents.publish('messages:refresh:list');
            },
            error => {
                this.handleError(error);
            });
        this.plt.activeSubscriptions.push(subscription);
    }

    /**
     * process sucess saving on backend
     *
     * @param response
     */
    private handleSuccess(response) {
        this.sending = false;
        // we have changed something for this chat group...
        this.edited = true;

        if (response.messageInvitations) {
            this.overlayService.showSuccess(response.messageInvitations);
        }

        this.onSended.emit(response);
        this.validate = false;

        if (this.modal) {
            // close modal if used..
            this.close();
        }
    }

    /**
     * process error when saving data on backend
     *
     * @param response
     */
    private handleError(response) {
        this.sending = false;
        const data = response.error;
        this.overlayService.showError(data.message);

        if (data.fields) {
            for (const field in data.fields) {
                if (data.fields.hasOwnProperty(field)) {
                    this.validationErrors[field] = data.fields[field].join(' ');
                }
            }
        }

        if (data.leave_group) {
            this.appEvents.publish('messages:refresh:list');
        }
    }

    /**
     * close modal window
     */
    public close() {
        // if attendee list for invitations is opened, just close it
        if (this.invitationtsOpen && this.chatGroup.id) {
            this.invitationtsOpen = false;
        } else {
            // otherwise close whoe modal
            this.modalController.dismiss({ action: 'close', chatGroupId: this.edited ? this.chatGroup.id : null });
        }
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        this.overlayService.closeModals();
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
